import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
  useFormikContext,
} from "formik";
import { Fragment, useState, useEffect, useContext, useCallback } from "react";
import CustomText from "../../atomic/CustomText";
import CloseIcon from "../../../assets/svg/CloseIcon";
import orderValidations from "../../../services/validations/orderValidations";
import Button from "../../organism/Button";
import { useOktaAuth } from "@okta/okta-react";
import { PopUpContext } from "../../../services/context/popupContext";
import useSaveAdditionalClinician from "../../../services/utils/custom hooks/useSaveAdditionalClinician";
import ErrorBoundary from "../ErrorBoundary";
import "../../atomic/style.css";

interface Clinician {
  firstName: string;
  lastName: string;
  npi: string;
  fax: string;
  email: string;
  isEditable: boolean;
  status?: string;
}

interface InitialValues {
  additionalClinician: Clinician[];
}

interface AdditionalClinicianCloseProps {
  setVisible: (visible: boolean) => void;
}

interface ClinicianFieldProps {
  clinician: Clinician;
  index: number;
  remove: (index: number) => void;
  onRemoveLast: () => void;
  handleClose: () => void;
}

interface FieldWithLabelProps {
  name: string;
  label: string;
  value: string;
  isEditable: boolean;
  placeholder: string;
  required?: boolean;
}

interface AdditionalClinicianSummaryProps {
  orderData: any;
  selectedTab: string;
}

const AdditionalClinicianClose: React.FC<AdditionalClinicianCloseProps> = ({
  setVisible,
}) => {
  const contactCsTeam = () => {
    // eslint-disable-next-line no-restricted-globals
    location.href = "mailto:clinicalservices@castlebiosciences.com";
    setVisible(false);
  };

  return (
    <div>
      <CustomText
        text={`Please contact CS Team to delete any additional clinician.`}
        fontWeight="font-normal"
      />
      <div className="flex flex-wrap items-end w-full mt-3">
        <div className="ml-auto flex">
          <Button
            buttonTheme="secondary"
            textInformation={{
              text: "contact cs team",
              fontWeight: "font-semibold",
            }}
            className="mr-3"
            onClick={contactCsTeam}
          />
          <Button
            buttonTheme="primary"
            textInformation={{
              text: "OKAY",
              fontWeight: "font-semibold",
            }}
            className="mr-3"
            onClick={() => setVisible(false)}
          />
        </div>
      </div>
    </div>
  );
};

const ClinicianField: React.FC<ClinicianFieldProps> = ({
  clinician,
  index,
  remove,
  onRemoveLast,
  handleClose,
}) => {
  const handleRemove = () => {
    if (!clinician.isEditable) {
      handleClose();
    } else {
      remove(index);
      onRemoveLast();
    }
  };

  return (
    <div className="" key={index}>
      <div className="focus:outline-primary bg-sideNavGray w-full p-6 my-5 shadow-sm border-[0.5px] rounded-md hover:border-primary hover:border overflow-auto break-all">
        <div className="flex flex-wrap justify-between mb-3">
          <div className="flex">
            <CustomText
              text={`Additional Clinician ${index + 1}`}
              fontWeight="font-medium"
            />
            {!clinician.isEditable && (
              <CustomText
                text={`${clinician.status}`}
                fontWeight="font-medium"
                className={`
                ${
                  clinician.status === "Pending"
                    ? "bg-[#d7defe] text-[#4F6CFE]"
                    : "bg-lightBlue text-primary"
                } 
                ml-3 py-1 px-2 block w-max capitalize text-center rounded-md !text-xs`}
              />
            )}
          </div>
          <button onClick={handleRemove}>
            <CloseIcon />
          </button>
        </div>
        <div className="flex flex-wrap mt-3">
          <div className={`${clinician.isEditable && "w-full"} flex`}>
            <FieldWithLabel
              name={`additionalClinician.${index}.firstName`}
              label="First Name"
              placeholder="Enter first name"
              required
              value={clinician.firstName}
              isEditable={clinician.isEditable}
            />
            <FieldWithLabel
              name={`additionalClinician.${index}.lastName`}
              label="Last Name"
              placeholder="Enter last name"
              required
              value={clinician.lastName}
              isEditable={clinician.isEditable}
            />
          </div>
          <div className={`${clinician.isEditable && "w-full"} flex`}>
            <FieldWithLabel
              name={`additionalClinician.${index}.npi`}
              label="NPI"
              required={false}
              placeholder="0000000000"
              value={clinician.npi}
              isEditable={clinician.isEditable}
            />
            <FieldWithLabel
              name={`additionalClinician.${index}.fax`}
              label="Fax"
              placeholder="000-000-0000"
              value={clinician.fax || "---"}
              isEditable={clinician.isEditable}
            />
          </div>
          <FieldWithLabel
            name={`additionalClinician.${index}.email`}
            label="Email"
            placeholder="Enter Email ID"
            value={clinician.email || "---"}
            isEditable={clinician.isEditable}
          />
        </div>
      </div>
    </div>
  );
};

const FieldWithLabel: React.FC<FieldWithLabelProps> = ({
  name,
  label,
  value,
  isEditable,
  required,
  placeholder,
}) => {
  const { setFieldTouched, setFieldValue, values } = useFormikContext<any>();

  const formatFaxNumber = useCallback((input: string): string => {
    const digits = input.replace(/\D/g, "");
    const limitedDigits = digits.slice(0, 10);

    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(
        3,
        6
      )}-${limitedDigits.slice(6)}`;
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = name?.includes("fax")
      ? formatFaxNumber(rawValue)
      : rawValue;
    setFieldValue(name, formattedValue);
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  return (
    <div className={`flex flex-col ${isEditable && "w-full my-1"} mr-5`}>
      <div className="flex items-center">
        <CustomText
          fontWeight="font-medium"
          fontSize={`${isEditable ? "text-xs" : ""}`}
          text={label}
        />
        <span className="ml-1">
          {isEditable && required ? (
            <span className="text-requiredRed">*</span>
          ) : (
            <span className="invisible">*</span>
          )}
        </span>
      </div>
      {isEditable ? (
        <Field
          name={name}
          type="text"
          placeholder={placeholder}
          value={
            values[name.split(".")[0]]?.[name.split(".")[1]]?.[
              name.split(".")[2]
            ] || ""
          } // Handle nested values
          onChange={handleChange}
          onBlur={handleBlur}
          className={`placeholder:text-[#B1B8C3] w-full roundedTextInput ${
            isEditable ? "text-sm" : ""
          }`}
        />
      ) : (
        <p>{value}</p>
      )}
      <div className="min-h-[10px]">
        {isEditable && (
          <ErrorMessage
            name={name}
            component="div"
            className="text-xs text-requiredRed mt-1"
          />
        )}
      </div>
    </div>
  );
};

const AdditionalClinicianSummary: React.FC<AdditionalClinicianSummaryProps> = ({
  orderData,
  selectedTab,
}) => {
  const [isAdditionalClinician, setIsAdditionalClinician] = useState(false);
  const [acknowledgement, setAcknowledgement] = useState(false);
  const { authState } = useOktaAuth();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleAdditionalClinicianChange = () => {
    setIsAdditionalClinician(!isAdditionalClinician);
    if (isAdditionalClinician) {
      setAcknowledgement(false);
    }
  };

  const handleAcknowledgementChange = () => {
    setAcknowledgement(!acknowledgement);
  };

  const { setVisible, setPopUp, setPopUpTitle } = useContext(PopUpContext);

  const handleClose = () => {
    setVisible(true);
    setPopUp(<AdditionalClinicianClose setVisible={setVisible} />);
    setPopUpTitle("Delete Additional Clinician");
  };

  const checkIfAllFieldsEmpty = (clinicians: any) => {
    return clinicians.every(
      (clinician: any) =>
        clinician.firstName === "" &&
        clinician.lastName === "" &&
        clinician.npi === "" &&
        clinician.fax === "" &&
        clinician.email === "" &&
        clinician.isEditable
    );
  };

  const isButtonDisabled = (
    errorsLength: number,
    values: InitialValues,
    submitting: boolean
  ) => {
    return (
      errorsLength > 0 ||
      submitting ||
      !values.additionalClinician.some((clinician) => clinician.isEditable) ||
      checkIfAllFieldsEmpty(values.additionalClinician)
    );
  };

  const [initialValues, setInitialValues] = useState<InitialValues>({
    additionalClinician: [
      {
        firstName: "",
        lastName: "",
        npi: "",
        fax: "",
        email: "",
        isEditable: true,
      },
    ],
  });

  const { mutate, isPending } = useSaveAdditionalClinician();
  useEffect(() => {
    if (orderData?.additionalClinician?.length > 0) {
      const mappedClinicians = orderData.additionalClinician.map(
        (clinician: any) => ({
          firstName: clinician.firstName || "",
          lastName: clinician.lastName || "",
          npi: clinician.npi || "",
          fax: clinician.fax || "",
          email: clinician.email || "",
          status: clinician.status || "",
          isEditable: false,
        })
      );
      setInitialValues({ additionalClinician: mappedClinicians });
    } else {
      setInitialValues({
        additionalClinician: [
          {
            firstName: "",
            lastName: "",
            npi: "",
            fax: "",
            email: "",
            isEditable: true,
          },
        ],
      });
    }

    if (orderData?.additionalClinician?.length > 0) {
      setAcknowledgement(true);
      setIsAdditionalClinician(true);
    }
  }, [orderData, acknowledgement]);

  return (
    <ErrorBoundary>
      <Formik
        initialValues={initialValues}
        validationSchema={orderValidations.additionalClinicianSchema}
        enableReinitialize
        onSubmit={(values) => {}}
      >
        {({ values, errors }) => (
          <Fragment>
            <div
              className={`py-8 z-50 uppercase bg-white tracking-[0.18em] ${
                selectedTab === "additional-info" && "sticky top-0"
              } w-full flex justify-between items-center`}
            >
              <CustomText
                fontWeight={"font-medium"}
                text={"Additional Info"}
                className={"w-full"}
                fontSize={"font-xl"}
              />

              <Button
                buttonTheme="primary"
                textInformation={{
                  text: "Save",
                  fontWeight: "font-semibold",
                }}
                className="mr-3"
                disabled={isButtonDisabled(
                  Object.keys(errors).length,
                  values,
                  submitting
                )}
                loading={submitting}
                onClick={() => {
                  setSubmitting(true);
                  mutate(
                    { values, orderId: orderData.id },
                    {
                      onSuccess: () => {
                        setSubmitting(false);
                        window.location.reload();
                      },
                      onError: () => {
                        setSubmitting(false);
                      },
                    }
                  );
                }}
              />
            </div>
            {/* <AdditionalClinicianBanner /> */}
            <div className="flex flex-col mt-4">
              <label
                htmlFor="additionalClinician"
                className="checkboxContainer block"
                onClick={handleAdditionalClinicianChange}
              >
                <input
                  type="checkbox"
                  name="additionalClinician"
                  checked={isAdditionalClinician}
                  disabled={orderData?.additionalClinician?.length > 0}
                  // disabled={true}
                  className="mr-2"
                />
                <span
                  className={`checkmark ${
                    orderData?.additionalClinician?.length > 0 &&
                    "cursor-not-allowed"
                  } `}
                />
                <span>Enter additional clinician</span>
              </label>

              {isAdditionalClinician && (
                <div className="flex items-center mt-3">
                  <label
                    htmlFor="acknowledgement"
                    className="checkboxContainer block"
                    onClick={handleAcknowledgementChange}
                  >
                    <input
                      type="checkbox"
                      name="acknowledgement"
                      checked={acknowledgement}
                      disabled={orderData?.additionalClinician?.length > 0}
                      className="mr-2"
                    />
                    <span
                      className={`checkmark ${
                        orderData?.additionalClinician?.length > 0 &&
                        "cursor-not-allowed"
                      }`}
                    />
                    <span>
                      {" "}
                      By checking this box, I hereby acknowledge this
                      clinician(s) will receive the final test report and
                      associated PHI.
                    </span>
                  </label>
                </div>
              )}
            </div>
            {acknowledgement && (
              <Form>
                <FieldArray name="additionalClinician">
                  {({ insert, remove, push }) => (
                    <div>
                      {values.additionalClinician.map((clinician, index) => (
                        <ClinicianField
                          key={index}
                          clinician={clinician}
                          index={index}
                          remove={remove}
                          onRemoveLast={() => {
                            if (values.additionalClinician.length === 1) {
                              setIsAdditionalClinician(false);
                              setAcknowledgement(false);
                            }
                          }}
                          handleClose={handleClose}
                        />
                      ))}
                      <Button
                        textInformation={{
                          text: "+ Add More",
                          fontWeight: "font-semibold",
                        }}
                        onClick={() =>
                          push({
                            firstName: "",
                            lastName: "",
                            npi: "",
                            fax: "",
                            email: "",
                            isEditable: true,
                          })
                        }
                        buttonTheme="transparent"
                        className="text-primary"
                      />
                    </div>
                  )}
                </FieldArray>
              </Form>
            )}
          </Fragment>
        )}
      </Formik>
    </ErrorBoundary>
  );
};

export default AdditionalClinicianSummary;
